// After the identity was confirmed: the server became unreachable. The app
// stays exactly where it is (same page, same confirmed identity); this banner
// says the data may be stale, shows the automatic retry, and offers an
// immediate retry. It disappears by itself once the server answers again.
import React from "react";
import { CloudOff, RefreshCw, WifiOff } from "lucide-react";
import { useSessionUi, useSecondsUntil, formatNumber, SessionStyles } from "./SessionUi";

export default function ConnectionBanner({ status, checking = false, nextRetryAt = null, onRetry }) {
  const ui = useSessionUi();
  const { palette, t, language } = ui;
  const seconds = useSecondsUntil(nextRetryAt);
  const offline = status === "offline";
  const Icon = offline ? WifiOff : CloudOff;

  // "Reconnecting…" while an attempt runs (or is about to), else the countdown.
  let detail = t.bannerReconnecting;
  if (!checking && seconds) detail = t.bannerRetryIn(formatNumber(seconds, language));

  return (
    <div
      dir={ui.dir}
      lang={language}
      role="status"
      aria-live="polite"
      className="nx-connection-banner"
      style={{
        position: "fixed",
        insetInline: 0,
        margin: "0 auto",
        width: "min(560px, calc(100% - 32px))",
        // Sits on the content, not above modals or the open sidebar drawer.
        zIndex: 999,
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 12px",
        paddingInlineStart: 14,
        background: palette.floating,
        color: palette.text,
        border: `1px solid ${palette.warningBorder}`,
        borderInlineStart: `3px solid ${palette.warning}`,
        borderRadius: 12,
      }}
    >
      <SessionStyles palette={palette} />
      <style>{`
        .nx-connection-banner { top: calc(4rem + 10px); }
        @media (max-width: 768px) { .nx-connection-banner { top: calc(3.5rem + 8px); } }
      `}</style>
      <Icon size={18} color={palette.warning} aria-hidden="true" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.45 }}>
          {offline ? t.bannerOffline : t.bannerUnavailable}
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.45, color: palette.textSecondary, marginTop: 2 }}>{detail}</div>
      </div>
      <button
        type="button"
        className="nx-session-btn nx-session-btn--secondary nx-session-btn--compact"
        onClick={onRetry}
        disabled={checking}
        style={{ flexShrink: 0 }}
      >
        <RefreshCw size={14} aria-hidden="true" className={checking ? "nx-session-spin" : undefined} />
        {t.bannerRetryNow}
      </button>
    </div>
  );
}
